import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users, Globe, Megaphone, Zap, CalendarCheck2, Mail, LineChart as LineChartIcon,
  Plus, Trash2, Copy, CheckCircle2, Clock, XCircle, Phone, Share2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import CareNetLink from "@/components/CareNetLink";
import { CARE_NET_LOGO } from "@/lib/careNet";

const EMPTY_PERSONA = { name: "", demographics: "", clinical_needs: "", motivations: "" };

function PatientReachCareNetCallout() {
  return (
    <CareNetLink
      className="mb-6 group flex items-center gap-4 rounded-xl border border-primary/15 bg-gradient-to-r from-[#F3FBF8] via-white to-[#F2F0FE] p-4 hover:shadow-md transition-all"
      data-testid="patient-reach-carenet-callout"
    >
      <img src={CARE_NET_LOGO} alt="" className="h-10 w-10 object-contain shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-widest text-emerald-800">Also in your Care Net</div>
        <div className="font-serif text-lg text-primary">Peer approvals keep acquisition cheap</div>
        <div className="text-xs text-muted-foreground">Verified vouches from colleagues follow you across Patient Reach — open the Care Net approval portal.</div>
      </div>
      <span className="text-primary group-hover:translate-x-1 transition-transform text-sm">Open ↗</span>
    </CareNetLink>
  );
}

export default function Forevency() {
  return (
    <AppShell
      pageTitle="Patient Reach · 7-Stage Acquisition System"
      pageDescription="Attract, engage and convert your ideal patients — from first search to loyal advocate — in seven stages."
    >
      <PatientReachCareNetCallout />
      <Tabs defaultValue="s1">
        <TabsList className="flex-wrap h-auto gap-1 bg-muted/30 p-1" data-testid="forevency-tabs">
          <TabsTrigger value="s1" data-testid="forevency-tab-s1"><Users size={13} className="mr-1" /> 1. Ideal Patient</TabsTrigger>
          <TabsTrigger value="s2" data-testid="forevency-tab-s2"><Globe size={13} className="mr-1" /> 2. Front Door</TabsTrigger>
          <TabsTrigger value="s3" data-testid="forevency-tab-s3"><Megaphone size={13} className="mr-1" /> 3. Omnichannel</TabsTrigger>
          <TabsTrigger value="s4" data-testid="forevency-tab-s4"><Zap size={13} className="mr-1" /> 4. Lead Capture</TabsTrigger>
          <TabsTrigger value="s5" data-testid="forevency-tab-s5"><CalendarCheck2 size={13} className="mr-1" /> 5. Booking</TabsTrigger>
          <TabsTrigger value="s6" data-testid="forevency-tab-s6"><Mail size={13} className="mr-1" /> 6. Nurture</TabsTrigger>
          <TabsTrigger value="s7" data-testid="forevency-tab-s7"><LineChartIcon size={13} className="mr-1" /> 7. Analyse</TabsTrigger>
        </TabsList>
        <div className="mt-6">
          <TabsContent value="s1"><Stage1 /></TabsContent>
          <TabsContent value="s2"><Stage2 /></TabsContent>
          <TabsContent value="s3"><Stage3 /></TabsContent>
          <TabsContent value="s4"><Stage4 /></TabsContent>
          <TabsContent value="s5"><Stage5 /></TabsContent>
          <TabsContent value="s6"><Stage6 /></TabsContent>
          <TabsContent value="s7"><Stage7 /></TabsContent>
        </div>
      </Tabs>
    </AppShell>
  );
}

// -----------------------------------------------------------------------
// Stage 1 — Ideal Patient & Value Proposition
// -----------------------------------------------------------------------
function Stage1() {
  const [state, setState] = useState({ personas: [], value_proposition: "" });
  const [vp, setVp] = useState("");
  const [draft, setDraft] = useState(EMPTY_PERSONA);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/patient-reach/stage1");
      setState({ personas: data.personas, value_proposition: data.value_proposition || "" });
      setVp(data.value_proposition || "");
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveVp = async () => {
    try { await api.put("/patient-reach/stage1/value-proposition", { value_proposition: vp });
      toast.success("Value proposition saved"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const addPersona = async () => {
    try { await api.post("/patient-reach/stage1/personas", draft); toast.success("Persona added");
      setDraft(EMPTY_PERSONA); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removePersona = async (id) => {
    try { await api.delete(`/patient-reach/stage1/personas/${id}`); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-white p-5">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Value Proposition</div>
        <div className="mt-1 font-serif text-xl text-primary">One sentence. Why should a patient choose you?</div>
        <div className="mt-3 flex gap-2">
          <Input value={vp} onChange={(e) => setVp(e.target.value)} maxLength={240}
                 placeholder="e.g. Fastest recovery time for hip replacements in Pune."
                 data-testid="forevency-vp-input" />
          <Button onClick={saveVp} disabled={!vp.trim()} data-testid="forevency-vp-save">Save</Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Ideal Patient Personas</div>
            <div className="mt-1 font-serif text-xl text-primary">Who you serve best (max 3)</div>
          </div>
          {state.personas.length < 3 && (
            <Button size="sm" onClick={() => setOpen(!open)} data-testid="forevency-persona-add-toggle">
              <Plus size={13} className="mr-1" /> Add persona
            </Button>
          )}
        </div>

        {open && (
          <div className="mt-4 rounded-md border border-dashed border-border p-4 bg-muted/20 space-y-3" data-testid="forevency-persona-form">
            <div><Label>Name</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Active Retiree Rohini" data-testid="forevency-persona-name" /></div>
            <div><Label>Demographics</Label><Textarea rows={2} value={draft.demographics} onChange={(e) => setDraft({ ...draft, demographics: e.target.value })} placeholder="Age / location / family / income band" data-testid="forevency-persona-demo" /></div>
            <div><Label>Clinical Needs</Label><Textarea rows={2} value={draft.clinical_needs} onChange={(e) => setDraft({ ...draft, clinical_needs: e.target.value })} placeholder="Conditions, procedures, screenings" data-testid="forevency-persona-clinical" /></div>
            <div><Label>Psychographic Motivations</Label><Textarea rows={2} value={draft.motivations} onChange={(e) => setDraft({ ...draft, motivations: e.target.value })} placeholder="e.g. independence, speed, trust" data-testid="forevency-persona-motiv" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={addPersona} disabled={!draft.name.trim()} data-testid="forevency-persona-save">Save persona</Button>
            </div>
          </div>
        )}

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {state.personas.length === 0 && !open && (
            <div className="col-span-full text-sm text-muted-foreground">No personas yet — start with your single most common patient type.</div>
          )}
          {state.personas.map((p) => (
            <div key={p.id} className="rounded-md border border-border p-4 bg-white" data-testid={`forevency-persona-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-serif text-lg text-primary">{p.name}</div>
                <button onClick={() => removePersona(p.id)} className="text-muted-foreground hover:text-rose-700" title="Delete">
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="mt-2 text-xs space-y-1">
                <div><span className="text-muted-foreground">Demographics —</span> <span className="text-primary">{p.demographics || "—"}</span></div>
                <div><span className="text-muted-foreground">Clinical —</span> <span className="text-primary">{p.clinical_needs || "—"}</span></div>
                <div><span className="text-muted-foreground">Motivations —</span> <span className="text-primary">{p.motivations || "—"}</span></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Stage 2 — Digital Front Door scorecard
// -----------------------------------------------------------------------
function Stage2() {
  const [state, setState] = useState(null);
  useEffect(() => {
    api.get("/patient-reach/stage2").then(({ data }) => setState(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!state) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const { scorecard, public_url } = state;
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-white p-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Digital Front Door Score</div>
            <div className="mt-1 font-serif text-5xl text-primary" data-testid="forevency-s2-score">{scorecard.score}</div>
            <div className="mt-1 text-sm text-muted-foreground">{scorecard.tier}</div>
          </div>
          <a href={public_url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">
            View your public page →
          </a>
        </div>
        <Progress value={scorecard.score} className="mt-4" />
      </div>
      <div className="rounded-lg border border-border bg-white p-5">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Conversion-readiness checks</div>
        <ul className="divide-y divide-border">
          {scorecard.checks.map((c) => (
            <li key={c.key} className="py-3 flex items-start gap-3" data-testid={`forevency-check-${c.key}`}>
              {c.pass ? <CheckCircle2 size={16} className="text-emerald-700 shrink-0 mt-0.5" />
                      : <XCircle size={16} className="text-rose-700 shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-primary">{c.label}</div>
                {!c.pass && c.hint && <div className="text-xs text-muted-foreground mt-0.5">{c.hint}</div>}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">w{c.weight}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Stage 3 — Omnichannel log + Review link
// -----------------------------------------------------------------------
function Stage3() {
  const [state, setState] = useState({ channels: [], known_channels: [] });
  const [draft, setDraft] = useState({ channel: "seo", period_start: "", period_end: "", spend: 0, leads: 0, booked: 0, note: "" });
  const [reviewUrl, setReviewUrl] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/patient-reach/stage3"); setState(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const log = async () => {
    try { await api.post("/patient-reach/stage3/log", { ...draft, spend: Number(draft.spend) || 0, leads: Number(draft.leads) || 0, booked: Number(draft.booked) || 0 });
      toast.success("Channel row logged"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const genReview = async () => {
    try { const { data } = await api.post("/patient-reach/stage3/review-request", {});
      setReviewUrl(data); toast.success("Review link ready");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const copy = () => { navigator.clipboard.writeText(reviewUrl?.copy_text || ""); toast.success("Copied"); };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-white p-5">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Log a channel period</div>
        <div className="mt-1 font-serif text-xl text-primary">What brought leads this week?</div>
        <div className="mt-3 grid gap-2 md:grid-cols-7">
          <Select value={draft.channel} onValueChange={(v) => setDraft({ ...draft, channel: v })}>
            <SelectTrigger data-testid="forevency-ch-channel"><SelectValue /></SelectTrigger>
            <SelectContent>
              {state.known_channels.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={draft.period_start} onChange={(e) => setDraft({ ...draft, period_start: e.target.value })} data-testid="forevency-ch-start" />
          <Input type="date" value={draft.period_end} onChange={(e) => setDraft({ ...draft, period_end: e.target.value })} data-testid="forevency-ch-end" />
          <Input type="number" placeholder="Spend ₹" value={draft.spend} onChange={(e) => setDraft({ ...draft, spend: e.target.value })} data-testid="forevency-ch-spend" />
          <Input type="number" placeholder="Leads" value={draft.leads} onChange={(e) => setDraft({ ...draft, leads: e.target.value })} data-testid="forevency-ch-leads" />
          <Input type="number" placeholder="Booked" value={draft.booked} onChange={(e) => setDraft({ ...draft, booked: e.target.value })} data-testid="forevency-ch-booked" />
          <Button onClick={log} data-testid="forevency-ch-save">Log</Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Reputation Management</div>
            <div className="mt-1 font-serif text-xl text-primary">Collect a review in one tap</div>
          </div>
          <Button onClick={genReview} data-testid="forevency-review-gen"><Share2 size={13} className="mr-1" /> Generate link</Button>
        </div>
        {reviewUrl && (
          <div className="mt-3 rounded-md border border-primary/20 bg-primary/5 p-3 flex items-center justify-between gap-2" data-testid="forevency-review-url">
            <code className="text-xs text-primary truncate">{reviewUrl.share_url}</code>
            <Button size="sm" variant="outline" onClick={copy}><Copy size={13} className="mr-1" /> Copy</Button>
          </div>
        )}
      </div>

      {state.channels.length > 0 && (
        <div className="rounded-lg border border-border bg-white p-5">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Recent channel logs</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-muted-foreground"><th>Channel</th><th>Window</th><th>Spend</th><th>Leads</th><th>Booked</th></tr></thead>
            <tbody>
              {state.channels.slice(0, 10).map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="py-2 text-primary">{c.channel}</td>
                  <td>{c.period_start} → {c.period_end}</td>
                  <td>₹{c.spend}</td><td>{c.leads}</td><td>{c.booked}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Stage 4 — Lead Capture Inbox + Embed snippet
// -----------------------------------------------------------------------
function Stage4() {
  const [state, setState] = useState({ leads: [], counts: {} });
  const [me, setMe] = useState(null); // { entity_type, entity_id }
  const [copied, setCopied] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/patient-reach/stage4/leads");
      setState(data);
      // Also fetch entity info once for the embed snippet
      if (!me) {
        try { const { data: s1 } = await api.get("/patient-reach/stage1");
          setMe({ entity_type: s1.entity.type, entity_id: s1.entity.id });
        } catch { /* ignore */ }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }, [me]);
  useEffect(() => { load(); }, [load]);
  const move = async (id, status) => {
    try { await api.post(`/patient-reach/stage4/leads/${id}/status`, { status, note: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const apiOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const snippet = me ? `<script src="${apiOrigin}/api/patient-reach/widget.js?entity_type=${me.entity_type}&entity_id=${me.entity_id}" defer></script>` : "";
  const copySnippet = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true); toast.success("Embed snippet copied");
      setTimeout(() => setCopied(false), 2500);
    });
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {["new", "contacted", "booked", "lost"].map((s) => (
          <div key={s} className="rounded-md border border-border bg-white p-4" data-testid={`forevency-kpi-${s}`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s}</div>
            <div className="font-serif text-2xl text-primary">{state.counts[s] || 0}</div>
          </div>
        ))}
      </div>

      {me && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-5" data-testid="forevency-embed-snippet">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-primary">Embed on your own site</div>
              <div className="mt-0.5 font-serif text-lg text-primary">Paste one line — leads land in this inbox</div>
              <p className="mt-1 text-xs text-muted-foreground">Drop this into your website's HTML (just before <code>&lt;/body&gt;</code>). A floating "Book a consultation" button appears and every submission arrives below within seconds.</p>
            </div>
            <Button size="sm" onClick={copySnippet} data-testid="forevency-embed-copy">
              <Copy size={13} className="mr-1" /> {copied ? "Copied!" : "Copy snippet"}
            </Button>
          </div>
          <pre className="mt-3 rounded-md bg-white border border-border p-3 text-[11px] overflow-x-auto whitespace-pre-wrap break-all"><code>{snippet}</code></pre>
          <div className="mt-2 text-[11px] text-muted-foreground">Works on WordPress, Wix, Squarespace, Framer, raw HTML — anywhere a <code>&lt;script&gt;</code> tag can go.</div>
        </div>
      )}
      <div className="rounded-lg border border-border bg-white overflow-hidden">
        {state.leads.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">No leads yet. Embed the Forevency widget on your site or share your public page.</div>
        ) : (
          <ul className="divide-y divide-border">
            {state.leads.map((L) => (
              <li key={L.id} className="p-4" data-testid={`forevency-lead-${L.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-primary">{L.name}</span>
                      <Badge variant="outline">{L.status}</Badge>
                      <span className="text-[11px] text-muted-foreground">· {L.source_channel}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Phone size={11} /> {L.phone}</span>
                      {L.email && <span>{L.email}</span>}
                      {L.condition && <span>· {L.condition}</span>}
                      <span>· {new Date(L.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {L.status !== "contacted" && <Button size="sm" variant="outline" onClick={() => move(L.id, "contacted")} data-testid={`forevency-lead-contacted-${L.id}`}>Contacted</Button>}
                    {L.status !== "booked" && <Button size="sm" onClick={() => move(L.id, "booked")} data-testid={`forevency-lead-booked-${L.id}`}>Booked</Button>}
                    {L.status !== "lost" && <Button size="sm" variant="ghost" onClick={() => move(L.id, "lost")} data-testid={`forevency-lead-lost-${L.id}`}>Lost</Button>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Stage 5 — Bookings
// -----------------------------------------------------------------------
function Stage5() {
  const [bk, setBk] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/patient-reach/stage5/bookings"); setBk(data.bookings || []); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, status) => {
    try { await api.post(`/patient-reach/stage5/bookings/${id}/decision`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="rounded-lg border border-border bg-white overflow-hidden">
      {bk.length === 0 ? (
        <div className="p-10 text-center text-sm text-muted-foreground">No booking requests yet.</div>
      ) : (
        <ul className="divide-y divide-border">
          {bk.map((b) => (
            <li key={b.id} className="p-4" data-testid={`forevency-booking-${b.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-primary">{b.name}</span>
                    <Badge variant="outline">{b.status}</Badge>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {b.phone} · prefers {b.preferred_date}
                    {b.reason && <> · {b.reason}</>}
                  </div>
                </div>
                {b.status === "requested" && (
                  <div className="flex items-center gap-1">
                    <Button size="sm" onClick={() => decide(b.id, "confirmed")} data-testid={`forevency-booking-confirm-${b.id}`}>Confirm</Button>
                    <Button size="sm" variant="ghost" onClick={() => decide(b.id, "declined")}>Decline</Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Stage 6 — Nurture status
// -----------------------------------------------------------------------
function Stage6() {
  return (
    <div className="rounded-lg border border-primary/15 bg-primary/5 p-6">
      <div className="text-[11px] uppercase tracking-widest text-primary">Automated Nurture</div>
      <div className="mt-1 font-serif text-xl text-primary">Reminders are running on their own</div>
      <ul className="mt-4 space-y-2 text-sm text-primary">
        <li className="flex items-start gap-2"><Clock size={14} className="mt-0.5" /> <span><b>48 h</b> before: SMS + email with the appointment details.</span></li>
        <li className="flex items-start gap-2"><Clock size={14} className="mt-0.5" /> <span><b>24 h</b> before: a shorter nudge with reschedule link.</span></li>
        <li className="flex items-start gap-2"><Clock size={14} className="mt-0.5" /> <span><b>Day-of</b>: directions, parking info and a last-mile reminder.</span></li>
      </ul>
      <div className="mt-4 text-xs text-muted-foreground">Reminders tick every 15 minutes for confirmed bookings. No action needed — just confirm bookings in Stage 5.</div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Stage 7 — Analyse & Optimise
// -----------------------------------------------------------------------
function Stage7() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/patient-reach/stage7").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const k = data.kpis;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Leads" value={k.total_leads} testid="s7-leads" />
        <Kpi label="Booked" value={k.booked} testid="s7-booked" />
        <Kpi label="CVR" value={`${k.cvr}%`} testid="s7-cvr" />
        <Kpi label="No-show" value={`${k.no_show_rate}%`} testid="s7-noshow" />
        <Kpi label="LTV est." value={`₹${k.ltv_estimate_inr || 0}`} testid="s7-ltv" />
      </div>
      <div className="rounded-lg border border-border bg-white p-5">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Channel efficiency</div>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-muted-foreground"><th>Channel</th><th>Spend</th><th>Leads</th><th>Booked</th><th>CVR</th><th>CAC</th></tr></thead>
          <tbody>
            {data.channels.map((c) => (
              <tr key={c.channel} className="border-t border-border" data-testid={`s7-channel-${c.channel}`}>
                <td className="py-2 text-primary">{c.channel}</td>
                <td>₹{c.spend}</td><td>{c.leads}</td><td>{c.booked}</td><td>{c.cvr}%</td>
                <td>{c.cac != null ? `₹${c.cac}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.recommendations.length > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5" data-testid="s7-recos">
          <div className="text-[11px] uppercase tracking-widest text-emerald-900 mb-2">Shift budget — recommendations</div>
          <ul className="list-disc list-inside text-sm text-emerald-900 space-y-1">
            {data.recommendations.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="rounded-md border border-border bg-white p-4" data-testid={`forevency-kpi-${testid}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2 font-serif text-2xl text-primary">{value}</div>
    </div>
  );
}
